import { createContext, useState, useContext, useEffect } from "react";
import { parsePreco } from "../utils/preco";

const CarrinhoContext = createContext();

// O carrinho fica salvo no navegador para não sumir ao recarregar a página ou reabrir o link.
const CHAVE_CARRINHO = "geniusmenu:carrinho";
const VALIDADE_CARRINHO_MS = 24 * 60 * 60 * 1000;

// Cada linha do carrinho é produto + observação: "sem cebola" e "com cebola" do mesmo
// lanche são linhas separadas. Carrinhos salvos antes disso não têm "chave" e a ganham aqui.
export const chaveItem = (id, observacao) => `${id}|${(observacao || "").trim()}`;
const comChave = (item) => (item.chave ? item : { ...item, chave: chaveItem(item.id, item.observacao) });

const lerCarrinhoSalvo = () => {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_CARRINHO) || "null");
    if (!salvo || !Array.isArray(salvo.itens)) return null;
    if (Date.now() - (salvo.atualizadoEm || 0) > VALIDADE_CARRINHO_MS) {
      localStorage.removeItem(CHAVE_CARRINHO);
      return null;
    }
    return { ...salvo, itens: salvo.itens.map(comChave) };
  } catch {
    return null;
  }
};

export const CarrinhoProvider = ({ children }) => {
  // Lê o localStorage uma vez só na inicialização.
  const [carrinhoSalvo] = useState(lerCarrinhoSalvo);
  const [carrinho, setCarrinho] = useState(() => carrinhoSalvo?.itens || []);
  // Restaurante dono dos itens do carrinho (o pedido vai para o WhatsApp dele).
  const [restauranteCarrinho, setRestauranteCarrinho] = useState(() => carrinhoSalvo?.restauranteId ?? null);

  useEffect(() => {
    try {
      if (carrinho.length === 0) {
        localStorage.removeItem(CHAVE_CARRINHO);
      } else {
        localStorage.setItem(CHAVE_CARRINHO, JSON.stringify({
          restauranteId: restauranteCarrinho,
          itens: carrinho,
          atualizadoEm: Date.now(),
        }));
      }
    } catch {
      // Sem acesso ao localStorage (modo privado/bloqueado): o carrinho segue só em memória.
    }
  }, [carrinho, restauranteCarrinho]);

  // Adiciona ao carrinho (soma na linha se já existe o mesmo produto com a mesma observação).
  const adicionarItem = (produto, restauranteId, { quantidade = 1, observacao = "" } = {}) => {
    if (restauranteId != null) setRestauranteCarrinho(String(restauranteId));
    const obs = (observacao || "").trim();
    const chave = chaveItem(produto.id, obs);
    const qtd = Math.max(1, Number(quantidade) || 1);
    setCarrinho((prev) => {
      const existente = prev.find((item) => item.chave === chave);
      if (existente) {
        return prev.map((item) => (item.chave === chave ? { ...item, quantidade: item.quantidade + qtd } : item));
      }
      return [...prev, { ...produto, preco: parsePreco(produto.preco), observacao: obs, chave, quantidade: qtd }];
    });
  };

  // Soma 1 numa linha que já está no carrinho.
  const incrementarItem = (chave) => {
    setCarrinho((prev) => prev.map((item) => (item.chave === chave ? { ...item, quantidade: item.quantidade + 1 } : item)));
  };

  // Tira uma unidade da linha (remove quando chega a zero).
  const removerItem = (chave) => {
    setCarrinho((prev) =>
      prev
        .map((item) => (item.chave === chave ? { ...item, quantidade: item.quantidade - 1 } : item))
        .filter((item) => item.quantidade > 0)
    );
  };

  const removerItemCompleto = (chave) => {
    setCarrinho((prev) => prev.filter((item) => item.chave !== chave));
  };

  const atualizarQuantidade = (chave, novaQuantidade) => {
    if (novaQuantidade <= 0) {
      removerItemCompleto(chave);
      return;
    }
    setCarrinho((prev) => prev.map((item) => (item.chave === chave ? { ...item, quantidade: novaQuantidade } : item)));
  };

  // Limpar carrinho
  const limparCarrinho = () => {
    setCarrinho([]);
    setRestauranteCarrinho(null);
  };

  // Calcular total do carrinho
  const calcularTotal = () => {
    return carrinho.reduce(
      (total, item) => total + parsePreco(item.preco) * item.quantidade,
      0
    );
  };

  // Calcular quantidade total de itens
  const calcularQuantidadeTotal = () => {
    return carrinho.reduce((total, item) => total + item.quantidade, 0);
  };

  return (
    <CarrinhoContext.Provider
      value={{
        carrinho,
        setCarrinho,
        restauranteCarrinho,
        adicionarItem,
        incrementarItem,
        removerItem,
        removerItemCompleto,
        atualizarQuantidade,
        limparCarrinho,
        calcularTotal,
        calcularQuantidadeTotal,
      }}
    >
      {children}
    </CarrinhoContext.Provider>
  );
};

export const useCarrinho = () => useContext(CarrinhoContext);